import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import {
    FileItem,
    Language,
    TranslatePricingInfo,
    TranslatePaymentInfo,
    TranslateUserInfo,
    TranslateSubscriptionInfo,
    TranslationMode,
} from '../types';
import { generateFileId } from '../utils/validation';

interface EnterpriseTranslationContextType {
    // Files
    files: FileItem[];
    addFiles: (newFiles: File[]) => void;
    removeFile: (fileId: string) => void;
    clearFiles: () => void;

    // File-level Translation Mode (per-file)
    setFileTranslationMode: (fileId: string, mode: TranslationMode) => void;
    setAllFilesTranslationMode: (mode: TranslationMode) => void;

    // Languages
    sourceLanguage: Language | null;
    targetLanguage: Language | null;
    setSourceLanguage: (language: Language | null) => void;
    setTargetLanguage: (language: Language | null) => void;

    // Pricing info from /translate endpoint
    pricingInfo: TranslatePricingInfo | null;
    setPricingInfo: (info: TranslatePricingInfo | null) => void;

    // Payment info from /translate endpoint
    paymentInfo: TranslatePaymentInfo | null;
    setPaymentInfo: (info: TranslatePaymentInfo | null) => void;

    // User info from /translate endpoint
    userInfo: TranslateUserInfo | null;
    setUserInfo: (info: TranslateUserInfo | null) => void;

    // Subscription info from /translate endpoint (enterprise only)
    subscriptionInfo: TranslateSubscriptionInfo | null;
    setSubscriptionInfo: (info: TranslateSubscriptionInfo | null) => void;

    // V2 Deferred Transaction Flow - Storage ID and stored files from /translate
    storageId: string | null;
    setStorageId: (id: string | null) => void;
    storedFiles: any[] | null; // Backend response format for stored files
    setStoredFiles: (files: any[] | null) => void;

    // Upload tracking
    hasCalledTranslate: boolean;
    setHasCalledTranslate: (called: boolean) => void;

    // Confirmation tracking (enterprise only)
    confirmationComplete: boolean;
    setConfirmationComplete: (confirmed: boolean) => void;

    // UI State
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
    error: string | null;
    setError: (error: string | null) => void;

    // Reset
    resetState: () => void;
}

const EnterpriseTranslationContext = createContext<EnterpriseTranslationContextType | undefined>(undefined);

export const useEnterpriseTranslation = () => {
    const context = useContext(EnterpriseTranslationContext);
    if (!context) {
        throw new Error('useEnterpriseTranslation must be used within an EnterpriseTranslationProvider');
    }
    return context;
};

interface EnterpriseTranslationProviderProps {
    children: ReactNode;
}

export const EnterpriseTranslationProvider: React.FC<EnterpriseTranslationProviderProps> = ({ children }) => {
    console.log('🏢 EnterpriseTranslationProvider initialized');

    // Files state
    const [files, setFiles] = useState<FileItem[]>([]);

    // Language state
    const [sourceLanguage, setSourceLanguage] = useState<Language | null>(null);
    const [targetLanguage, setTargetLanguage] = useState<Language | null>(null);

    // Pricing info state
    const [pricingInfo, setPricingInfo] = useState<TranslatePricingInfo | null>(null);

    // Payment info state
    const [paymentInfo, setPaymentInfo] = useState<TranslatePaymentInfo | null>(null);

    // User info state
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [userInfo, setUserInfo] = useState<TranslateUserInfo | null>(null);

    // Subscription info state (enterprise only)
    const [subscriptionInfo, setSubscriptionInfo] = useState<TranslateSubscriptionInfo | null>(null);

    // V2 Deferred Transaction Flow state
    const [storageId, setStorageId] = useState<string | null>(null);
    const [storedFiles, setStoredFiles] = useState<any[] | null>(null);

    // Upload tracking state
    const [hasCalledTranslate, setHasCalledTranslate] = useState<boolean>(false);

    // Confirmation tracking state (enterprise only)
    const [confirmationComplete, setConfirmationComplete] = useState<boolean>(false);

    // UI state
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // File management functions
    const addFiles = useCallback(
        (newFiles: File[]) => {
            console.log('📁 Adding files to enterprise context:', {
                count: newFiles.length,
                files: newFiles.map((f) => ({ name: f.name, size: f.size, type: f.type })),
            });

            const fileItems: FileItem[] = newFiles.map((file) => ({
                id: generateFileId(),
                file,
                name: file.name,
                size: file.size,
                type: file.type,
                translationMode: 'automatic' as TranslationMode, // Default mode for new files
            }));
            setFiles((prev) => [...prev, ...fileItems]);
            console.log('✅ Files added successfully, total files:', files.length + fileItems.length);
        },
        [files.length],
    );

    // Set translation mode for a specific file
    const setFileTranslationMode = useCallback((fileId: string, mode: TranslationMode) => {
        console.log(`🔄 Setting translation mode for file ${fileId} to ${mode}`);
        setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, translationMode: mode } : f)));
    }, []);

    // Set translation mode for all files
    const setAllFilesTranslationMode = useCallback((mode: TranslationMode) => {
        console.log(`🔄 Setting translation mode for all files to ${mode}`);
        setFiles((prev) => prev.map((f) => ({ ...f, translationMode: mode })));
    }, []);

    const removeFile = useCallback((fileId: string) => {
        console.log('🗑️ Removing file from enterprise context:', fileId);
        setFiles((prev) => {
            const fileToRemove = prev.find((f) => f.id === fileId);
            if (fileToRemove?.file) {
                // Clean up any object URLs created for this file
                try {
                    const objectUrl = (fileToRemove.file as any).objectURL;
                    if (objectUrl && typeof objectUrl === 'string') {
                        URL.revokeObjectURL(objectUrl);
                    }
                } catch (error) {
                    console.warn('⚠️ Failed to revoke object URL:', error);
                }
            }
            return prev.filter((f) => f.id !== fileId);
        });
        console.log('✅ File removed successfully');
    }, []);

    const clearFiles = useCallback(() => {
        console.log('🧹 Clearing all files from enterprise context');
        setFiles((prev) => {
            // Clean up object URLs for all files before clearing
            prev.forEach((fileItem) => {
                if (fileItem.file) {
                    try {
                        const objectUrl = (fileItem.file as any).objectURL;
                        if (objectUrl && typeof objectUrl === 'string') {
                            URL.revokeObjectURL(objectUrl);
                        }
                    } catch (error) {
                        console.warn('⚠️ Failed to revoke object URL:', error);
                    }
                }
            });
            return [];
        });
        console.log('✅ All files cleared');
    }, []);

    // Reset function with proper cleanup
    const resetState = useCallback(() => {
        console.log('🔄 Resetting enterprise translation state');
        // Clean up files first
        clearFiles();
        setSourceLanguage(null);
        setTargetLanguage(null);
        setPricingInfo(null);
        setPaymentInfo(null);
        setUserInfo(null);
        setSubscriptionInfo(null);
        setStorageId(null);
        setStoredFiles(null);
        setHasCalledTranslate(false);
        setConfirmationComplete(false);
        setIsLoading(false);
        setError(null);
        console.log('✅ State reset complete');
    }, [clearFiles]);

    const value: EnterpriseTranslationContextType = useMemo(
        () => ({
            files,
            addFiles,
            removeFile,
            clearFiles,
            setFileTranslationMode,
            setAllFilesTranslationMode,
            sourceLanguage,
            targetLanguage,
            setSourceLanguage,
            setTargetLanguage,
            pricingInfo,
            setPricingInfo,
            paymentInfo,
            setPaymentInfo,
            userInfo,
            setUserInfo,
            subscriptionInfo,
            setSubscriptionInfo,
            storageId,
            setStorageId,
            storedFiles,
            setStoredFiles,
            hasCalledTranslate,
            setHasCalledTranslate,
            confirmationComplete,
            setConfirmationComplete,
            isLoading,
            setIsLoading,
            error,
            setError,
            resetState,
        }),
        [
            files,
            addFiles,
            removeFile,
            clearFiles,
            setFileTranslationMode,
            setAllFilesTranslationMode,
            sourceLanguage,
            targetLanguage,
            pricingInfo,
            paymentInfo,
            userInfo,
            subscriptionInfo,
            storageId,
            storedFiles,
            hasCalledTranslate,
            confirmationComplete,
            isLoading,
            error,
            resetState,
        ],
    );

    return <EnterpriseTranslationContext.Provider value={value}>{children}</EnterpriseTranslationContext.Provider>;
};
